import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import { 
  MapPinIcon, 
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface Package {
  _id: string;
  name: string;
  slug: string;
  description: string;
  bannerImage: string;
  priceUSD: number;
  priceAUD: number;
  lastMinuteDealPriceUSD?: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
  hasActiveLastMinuteDeal?: boolean;
  validityPeriod: number;
  resorts: Array<{
    _id: string;
    name: string;
    destination: { _id: string; name: string };
  }>;
  highlights: string[];
  isActive: boolean;
  isLive: boolean;
  destination?: { _id: string; name: string; showInMainNav?: boolean };
}

interface Destination {
  _id: string;
  name: string;
  showInMainNav?: boolean;
}

// Normalize image URLs to work in dev (3000->5000) and prod
const resolveImageUrl = (img?: string) => {
  if (!img) return '';
  const normalized = String(img).replace(/\\/g, '/');
  // If absolute URL or data URL, return as-is
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }
  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

const PackagesPage: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const { formatPrice, currency } = useCurrency();

  // Helper to get effective AUD price for sorting
  const getEffectiveAUDPrice = (pkg: Package) => {
    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
    
    if (hasDealPrice && isDealActive && dealPriceAUD) {
      return dealPriceAUD;
    }
    return pkg.priceAUD;
  };

  // Helper function to get the display price in AUD only (last minute deal if available, otherwise regular price)
  const getDisplayPrice = (pkg: Package) => {
    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
    
    if (hasDealPrice && isDealActive && dealPriceAUD) {
      return {
        price: `A$${dealPriceAUD.toLocaleString()}`,
        originalPrice: `A$${pkg.priceAUD.toLocaleString()}`,
        isDeal: true,
        savings: `A$${(pkg.priceAUD - dealPriceAUD).toFixed(0)}`
      };
    }
    return {
      price: `A$${pkg.priceAUD.toLocaleString()}`,
      originalPrice: null,
      isDeal: false,
      savings: null
    };
  };
  

  // Helper to get package's primary destination ID
  const getPackageDestinationId = (pkg: Package): string => {
    // First check if package has direct destination
    if (pkg.destination?._id) {
      return pkg.destination._id;
    }
    // Otherwise get from first resort's destination
    if (pkg.resorts && pkg.resorts.length > 0 && pkg.resorts[0].destination?._id) {
      return pkg.resorts[0].destination._id;
    }
    return '';
  };

  // Fast API loading with timeout
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        
        // Fetch both packages and destinations in parallel
        const [packagesRes, destinationsRes] = await Promise.all([
          axios.get(`${apiUrl}/api/packages`),
          axios.get(`${apiUrl}/api/destinations`)
        ]);
        
        const packagesData = packagesRes.data;
        const destinationsData = destinationsRes.data;
        
        const list: Package[] = Array.isArray(packagesData) ? packagesData : (packagesData.packages || []);
        const destList: Destination[] = Array.isArray(destinationsData) ? destinationsData : (destinationsData.destinations || []);
        
        setDestinations(destList);
        
        // Create a set of main nav destination IDs
        const mainNavDestIds = new Set(
          destList.filter(d => d.showInMainNav === true).map(d => d._id)
        );
        
        // Show only live and active packages
        const livePackages = list.filter((pkg: Package) => (pkg.isLive === true) && (pkg.isActive === true));
        
        // Sort packages: main nav destinations first, then others, each group by price
        const sortedPackages = livePackages.sort((a, b) => {
          const destIdA = getPackageDestinationId(a);
          const destIdB = getPackageDestinationId(b);
          
          const aIsMainNav = mainNavDestIds.has(destIdA);
          const bIsMainNav = mainNavDestIds.has(destIdB);
          
          // Main nav destinations come first
          if (aIsMainNav && !bIsMainNav) return -1;
          if (!aIsMainNav && bIsMainNav) return 1;
          
          // Within same group, sort by effective AUD price (lowest first)
          return getEffectiveAUDPrice(a) - getEffectiveAUDPrice(b);
        });
        
        setPackages(sortedPackages);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
        // Show empty state on error - no dummy fallback
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filters removed as per request – show all live packages without search/sort UI

  return (
    <>
      <Helmet>
        <title>Bali Resort Packages - Choose Your Perfect Getaway | Bali Getaway</title>
        <meta name="description" content="Discover our curated collection of Bali resort packages. From luxury retreats to adventure packages, find your perfect Bali experience with flexible vouchers." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white container-max px-4">
          <h1 className="text-5xl md:text-6xl font-bold font-display mb-4">
            Resort Packages
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Curated experiences for every type of traveler
          </p>
        </div>
      </section>

    {/* Filter/Search removed */}

      {/* Package Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          {packages.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🏝️</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">No packages found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
              <button
                onClick={() => { /* no-op: filters removed */ }}
                className="btn-primary"
              >
                Show All Packages
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8">
              {loading ? (
                <div className="col-span-full text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading packages...</p>
                </div>
              ) : (
                packages.map((pkg) => (
                  <div key={pkg._id} className="card overflow-hidden group hover:shadow-2xl transition-all duration-300">
                    <div className="relative overflow-hidden">
                      <img 
                        src={resolveImageUrl(pkg.bannerImage) || '/api/placeholder/400/300'} 
                        alt={pkg.name}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/api/placeholder/400/300';
                        }}
                      />
                      <div className="absolute top-4 right-4">
                        <div className="bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                          {pkg.isLive ? 'Live' : 'Preview'}
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-orange-600 transition-colors">
                        {pkg.name}
                      </h3>
                      
                      {pkg.resorts.length > 0 && (
                        <div className="flex items-center text-gray-600 mb-3">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          <span className="text-sm">
                            {Array.from(new Set(pkg.resorts.map((resort: any) => 
                              resort.destination?.name || resort.name
                            ).filter(Boolean))).join(', ')}
                          </span>
                        </div>
                      )}
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {pkg.description}
                      </p>
                      
                      <div className="mb-4">
                        {(() => {
                          const displayPrice = getDisplayPrice(pkg);
                          return (
                            <div>
                              {displayPrice.isDeal && (
                                <div className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                                  ⚡ LAST MINUTE DEAL - Save {displayPrice.savings}
                                </div>
                              )}
                              <div className="flex items-baseline gap-2 mb-1">
                                <div className={`text-2xl font-bold ${displayPrice.isDeal ? 'text-red-600' : 'text-gray-900'}`}>
                                  {displayPrice.price}
                                </div>
                                {displayPrice.originalPrice && (
                                  <div className="text-lg text-gray-500 line-through">
                                    {displayPrice.originalPrice}
                                  </div>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">
                                Valid for {pkg.validityPeriod} months
                              </div>
                            </div>
                          );
                        })()
                        }
                      </div>

                      <div className="space-y-2 mb-6">
                        {pkg.highlights.slice(0, 3).map((highlight, index) => (
                          <div key={index} className="flex items-center text-sm text-gray-600">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                            <span>
                              {typeof highlight === 'string' ? highlight : (highlight as any)?.title || 'Highlight'}
                            </span>
                          </div>
                        ))}
                        {pkg.highlights.length > 3 && (
                          <div className="text-sm text-orange-600 font-medium">
                            +{pkg.highlights.length - 3} more features
                          </div>
                        )}
                      </div>

                      <Link 
                        to={`/packages/${pkg._id}`}
                        className="btn-primary w-full text-center block"
                      >
                        View Package Details
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Mobile filter modal removed */}

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-yellow-600 to-orange-600 text-white">
        <div className="container-max text-center">
          <h2 className="text-4xl font-bold font-display mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our travel experts can create a custom package tailored to your preferences and budget.
          </p>
          <Link 
            to="/contact" 
            className="btn-secondary"
          >
            Contact Our Experts
          </Link>
        </div>
      </section>
    </>
  );
};

export default PackagesPage;
